import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../../css/centeredContainer.css';

import axios from '../../api/axios';
import { useTranslation } from 'react-i18next';
import useAuth from '../../hooks/useAuth';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import '../../css/Home.css';

import LoadingSpinner from '../loadingSpinner/LoadingSpinner';
import Error from './Error';

export default function Home({ isNavOpen }) {
  const { t } = useTranslation();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const { auth, authLoaded } = useAuth();
  const isAdmin = auth?.roles?.includes('Admin');
  const canEditSchool = auth?.userId <= 2;
  const isSuperAdmin = auth?.roles?.includes('SuperAdmin');
  const loggedInUser = auth?.userId;
  const navigate = useNavigate();

  console.log('AUTH DEBUG', auth, authLoaded);

  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);

  const fallbackLogo = `${backendUrl}/media/home/dnzify-logo-black-horizontal.png`;

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 1.2 } }
  };

  useEffect(() => {
    if (!backendUrl) return;

    const fetchSchoolData = async () => {
      setLoading(true);
      try {
        const response = await axios.get('/school');
        const schoolData = response.data[0] || null;
        setSchool(schoolData);

        if (!schoolData) return;

        // Fetch latest media file in each folder
        const fetchLatestMedia = async (type) => {
          try {
            const res = await axios.get(`/school/home/${type}/latest`);
            return res.data?.url ? `${backendUrl}${res.data.url}?t=${Date.now()}` : null;
          } catch {
            return null;
          }
        };


        const [logo, video, image] = await Promise.all([
          fetchLatestMedia('logo'),
          fetchLatestMedia('video'),
          fetchLatestMedia('image'),
        ]);

        setLogoUrl(logo);
        setVideoUrl(video);
        setImageUrl(image);

      } catch (err) {
        console.error('Error fetching school data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSchoolData();
  }, [backendUrl]);

  return (
    <div className={isNavOpen ? 'body-squeezed' : 'body'}>


      {/* Admin Edit Button (top-right corner) */}
      {isAdmin && canEditSchool && (
        <div className='edit-home-button-topright' onClick={() => navigate('/admin/homeadmin')}>
          <FontAwesomeIcon icon={faEdit} />
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : school ? (
        <>

          {/* SCHOOL LOGO */}
          {logoUrl && (
            <motion.div
              className="school-logo-section"
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <img src={logoUrl} alt="School Logo" className="school-logo" />
            </motion.div>
          )}



          {/* VIDEO SECTION */}
          {videoUrl && (
            <section className="home-media-section video">
              <video
                src={videoUrl}
                autoPlay
                muted
                loop
                playsInline
              />
            </section>
          )}



          {/* FIRST TEXT SECTION (School Name + Tagline) */}
          <motion.section
            className="text-section"
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h1>{school.school_name}</h1>
            {school.tagline && <h2>{school.tagline}</h2>}
          </motion.section>

          {/* IMAGE SECTION */}
          {imageUrl && (
            <section className="home-media-section image">
              <img src={imageUrl} alt="School" />
            </section>
          )}

          {/* SECOND TEXT SECTION (Homepage Text) */}
          {school.homepage_text && (
            <motion.section
              className="text-section tx1"
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <p>{school.homepage_text}</p>
            </motion.section>
          )}

          {/* CONTACT / FOOTER */}
          <div className="footer-info">
            {[school.phone_number, school.address].filter(Boolean).join(' | ')}

            {school.student_email && (
              <>
                {school.phone_number || school.address ? ' | ' : ''}
                <a href={`mailto:${school.student_email}`}>{school.student_email}</a>
              </>
            )}

            {school.website_url && (
              <>
                {school.phone_number || school.address || school.student_email ? ' | ' : ''}
                <a href={school.website_url} target="_blank" rel="noopener noreferrer">
                  {school.website_url.replace(/^https?:\/\//, '')}
                </a>
              </>
            )}

            {(school.facebook_url || school.instagram_url) && ' | '}

            {school.facebook_url && (
              <a
                href={school.facebook_url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="icon-inline facebook"
              >
                <FontAwesomeIcon icon={faFacebook} />
              </a>
            )}
            {school.instagram_url && (
              <a
                href={school.instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="icon-inline instagram"
              >
                <FontAwesomeIcon icon={faInstagram} />
              </a>
            )}
          </div>

          {logoUrl && (
            <motion.div
              className="school-logo-section"
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {school.website_url ? (
                <a href={school.website_url} target="_blank" rel="noopener noreferrer">
                  <img src={logoUrl} alt="School Logo" className="school-logo" />
                </a>
              ) : (
                <img src={logoUrl} alt="School Logo" className="school-logo" />
              )}
            </motion.div>
          )}



          <div className="home-footer">
            <a href="https://www.dnzify.com" target="_blank" rel="noopener noreferrer">
              <img
                src={fallbackLogo}
                alt="DNZIFY Logo"
                className="dnzify-logo-centered-footer"
              />
            </a>
          </div>
        </>
      ) : (
        <Error />
      )}


    </div>
  );

}
